// Turns a tree node's SQL condition into plain English.
//
// Nodes carry their logic as SQL: `splitConditions` is the test at a node,
// `sumConditions` is the whole path down to it. Both are shown raw today, which
// reads fine for `employees >= 5000` and not at all for a six-clause path.
//
// Parsed with node-sql-parser, already a dependency and already used server-side
// to validate observation SELECTs (RGI-1779). A regex over SQL text cannot tell a
// keyword from the same word inside a string literal, which is the exact bug that
// ticket fixed, so this does not hand-roll one.
//
// Imported from the dialect-specific build on purpose: the package index carries
// every dialect and costs 1.75 MB in the client bundle, where postgresql alone is
// 253 KB (49 KB gzipped). Redshift is Postgres-derived, so postgresql is also the
// correct grammar, the same choice the server-side validator made.
import { Parser } from 'node-sql-parser/build/postgresql';

/**
 * One piece of a described condition. The AST already separates the column from
 * the literal, so which words name a field and which name a value is known
 * exactly rather than guessed at, and the UI can mark them apart.
 */
export interface TraitPart {
  kind: 'field' | 'value' | 'word';
  text: string;
}

export interface ConditionTrait {
  /** Plain English, e.g. "employees 5,000 or more". */
  text: string;
  /** The same text, split into field, value and connecting words. */
  parts: TraitPart[];
  /** Set only when the SQL could not be parsed and the text IS the raw SQL. */
  unparsed?: boolean;
}

// The library's expression types are a loose union, so narrow by shape instead of
// claiming to model the whole grammar. Every field here was observed on real
// output for the operators below.
interface Expr {
  type: string;
  operator?: string;
  left?: Expr;
  right?: Expr;
  expr?: Expr;
  column?: string;
  name?: string;
  args?: Expr;
  parentheses?: boolean;
  value?: string | number | boolean | null | Expr[];
}

// One parsed comparison, kept structured so range pairs on the same column can be
// merged before anything is turned into a string.
interface Comparison {
  column: string;
  label: string;
  operator: string;
  value: string;
  /** Every literal on the right, so an IN list marks each value on its own. */
  values?: string[];
  numeric?: number;
}

const parser = new Parser();
const numberFormat = new Intl.NumberFormat('en-US');

const BOOLEAN_VALUES = ['0', '1', 'true', 'false'];

const field = (text: string): TraitPart => ({ kind: 'field', text });
// A stored value is often a slug: technographic buckets are `apache_http_server`,
// `google_tag_manager`. The underscores are storage syntax, not part of the name, so
// they become spaces. That is as far as this goes: title-casing would assert a
// capitalisation the data does not carry, and it gets `mysql`, `postgresql` and
// `github` wrong. Nothing here reaches the SQL, which is rebuilt from the values
// themselves.
const val = (text: string): TraitPart => ({
  kind: 'value',
  text: text.replace(/_/g, ' '),
});
const word = (text: string): TraitPart => ({ kind: 'word', text });

/**
 * A label per column, overriding the name this file would derive from the column
 * itself. rgip sends one with every split candidate, out of the label dictionary it
 * keeps in sync with studio-cli, which knows that `hg__hqcountry` is "HQ Country" and
 * that `tag_is_b2b_enterprise` asserts "B2B enterprise" rather than being a field
 * called "tag is b2b enterprise".
 */
export type ColumnLabels = Record<string, string>;

/** Values with "or" between them, so each one is marked on its own. */
function orList(values: string[]): TraitPart[] {
  return values.flatMap((value, i) =>
    i ? [word('or'), val(value)] : [val(value)]
  );
}

// The colon rides on the field rather than standing alone, because parts are
// joined with a space and "company type : public" is not what this used to say.
const COMPARISONS: Record<string, (c: Comparison) => TraitPart[]> = {
  '=': (c) => [field(`${c.label}:`), val(c.value)],
  '<>': (c) => [field(c.label), word('other than'), val(c.value)],
  '!=': (c) => [field(c.label), word('other than'), val(c.value)],
  '>=': (c) => [field(c.label), val(c.value), word('or more')],
  '>': (c) => [field(c.label), word('over'), val(c.value)],
  '<=': (c) => [field(c.label), val(c.value), word('or less')],
  '<': (c) => [field(c.label), word('under'), val(c.value)],
  IN: (c) => [field(c.label), word('in'), ...orList(c.values ?? [c.value])],
  'NOT IN': (c) => [
    field(c.label),
    word('not in'),
    ...orList(c.values ?? [c.value]),
  ],
};

// Column names in real models are double-underscored (`company__type`), so the
// collapse has to run after the underscore swap or every label reads with a gap.
//
// A supplied label wins outright. Deriving one from the column is a fallback, not a
// preference: it is all this file has for a tree node, and it is what showed the
// customer "it spend bucket" and "company category industry group".
function humanize(column: string, labels?: ColumnLabels): string {
  const supplied = labels?.[column]?.trim();
  if (supplied) return supplied;
  return column.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
}

// Conditions are written case-insensitively as LOWER(col) = LOWER('value'). That
// is an implementation detail of the comparison, not part of what the node means,
// so it is stripped before anything is described. Without this both sides of the
// comparison come back empty and the trait renders as a bare "=".
const CASE_FUNCTIONS = ['LOWER', 'UPPER', 'TRIM', 'BTRIM', 'INITCAP'];

function firstArg(node: Expr): Expr | undefined {
  const args = node.args?.value;
  return Array.isArray(args) ? args[0] : undefined;
}

function unwrap(node?: Expr): Expr | undefined {
  if (node?.type !== 'function') return node;
  const name = (node.name ?? '').toUpperCase();
  if (!CASE_FUNCTIONS.includes(name)) return node;
  return unwrap(firstArg(node)) ?? node;
}

function isNot(node?: Expr): boolean {
  return node?.type === 'function' && (node.name ?? '').toUpperCase() === 'NOT';
}

// Negating by inverting the operator reads far better than wrapping the whole
// clause: "employees under 5,000" rather than "not (employees 5,000 or more)".
const NEGATED_OPERATOR: Record<string, string> = {
  '=': '<>',
  '<>': '=',
  '!=': '=',
  '>=': '<',
  '>': '<=',
  '<=': '>',
  '<': '>=',
  IN: 'NOT IN',
  'NOT IN': 'IN',
  IS: 'IS NOT',
  'IS NOT': 'IS',
  LIKE: 'NOT LIKE',
  'NOT LIKE': 'LIKE',
};

// Booleans are stored as numbers (there is no boolean ComputationType), so a 0/1
// equality is read as a fact rather than a comparison: "raised capital" instead of
// "raised capital: 1". Known and accepted trade-off: a genuine count compared to 1,
// like `is_hiring_aes = 1` meaning "hiring exactly one AE", is misstated as a
// boolean. Nothing in the SQL or the computation metadata can tell the two apart.
// Convention rather than structure, and the one inference in this file that is
// not read off the AST: a boolean column often names its value, as in
// `geo_is_sf_bay`. There is no literal in the SQL to point at, so the value is
// taken from the column name. It only decides which words are marked as the
// value, never what the text says, which is why a guess is acceptable here.
const NAMED_VALUE = /^(.+?) (is|has) (.+)$/;

function booleanFact(label: string, affirmative: boolean): TraitPart[] {
  const named = NAMED_VALUE.exec(label);
  if (named) {
    const [, subject, verb, value] = named;
    const negative = verb === 'has' ? 'has no' : 'is not';
    return [field(subject), word(affirmative ? verb : negative), val(value)];
  }
  if (affirmative) return [field(label)];
  if (label.startsWith('is '))
    return [field('is'), word('not'), field(label.slice(3))];
  if (label.startsWith('has '))
    return [field('has'), word('not'), field(label.slice(4))];
  return [word('not'), field(label)];
}

// A year is a number that must not be punctuated: "founded year other than 2,000"
// reads as a quantity when it means 2000 AD.
function isYearColumn(label: string): boolean {
  return /(^|\s)years?(\s|$)/.test(label);
}

function literal(raw?: Expr, bare = false): string {
  const node = unwrap(raw);
  if (!node) return '';
  if (node.type === 'number') {
    return bare ? String(node.value) : numberFormat.format(Number(node.value));
  }
  if (node.type === 'null') return 'null';
  if (node.type === 'expr_list' && Array.isArray(node.value)) {
    return node.value.map((item) => literal(item, bare)).join(' or ');
  }
  return String(node.value ?? '');
}

function literalList(raw?: Expr, bare = false): string[] {
  const node = unwrap(raw);
  if (node?.type === 'expr_list' && Array.isArray(node.value)) {
    return node.value.map((item) => literal(item, bare));
  }
  return [literal(raw, bare)];
}

// Split on AND only. An OR stays whole, because breaking it into separate traits
// would state each branch as required when only one of them is.
function flattenAnd(expr?: Expr): Expr[] {
  if (
    expr?.type === 'binary_expr' &&
    expr.operator === 'AND' &&
    !expr.parentheses
  ) {
    return [...flattenAnd(expr.left), ...flattenAnd(expr.right)];
  }
  return expr ? [expr] : [];
}

/**
 * The operands of an AND or OR node that is already known to be one.
 *
 * Always descends into the node's own children, which flattenAnd cannot do: it
 * tests the node it is given, so handing it a parenthesised node returns that
 * same node and the caller describes it again forever. A real condition ends in
 * a bracketed OR often enough that this turned a whole node definition into
 * unparsed SQL, because the stack overflow surfaced as a caught parse failure.
 */
function flattenOperands(expr: Expr, operator: string): Expr[] {
  return [expr.left, expr.right].flatMap((side) => {
    if (!side) return [];
    return side.type === 'binary_expr' && side.operator === operator
      ? flattenOperands(side, operator)
      : [side];
  });
}

/**
 * `x = 'a' OR x = 'b'` is a list of allowed values, and its negation is a list
 * of excluded ones, so both are collapsed into the IN form the parser already
 * renders for a real IN. Without this, a negated OR came out as one opaque
 * "not (...)" run with no field or value to mark inside it.
 */
function collapseSameColumn(
  sides: (Comparison | TraitPart[])[],
  joinOperator: string
): Comparison | null {
  const wanted = joinOperator === 'OR' ? ['=', 'IN'] : ['<>', '!=', 'NOT IN'];
  const target = joinOperator === 'OR' ? 'IN' : 'NOT IN';
  if (sides.length < 2) return null;
  const first = sides[0];
  if (Array.isArray(first)) return null;
  const same = sides.every(
    (side) =>
      !Array.isArray(side) &&
      side.column === first.column &&
      wanted.includes(side.operator)
  );
  if (!same) return null;
  const values = (sides as Comparison[]).flatMap(
    (side) => side.values ?? [side.value]
  );
  return {
    column: first.column,
    label: first.label,
    operator: target,
    value: values.join(' or '),
    values,
  };
}

// A LIKE pattern says something different depending on where the wildcards sit.
function describeLike(
  label: string,
  pattern: string,
  negated: boolean
): TraitPart[] {
  const starts = pattern.startsWith('%');
  const ends = pattern.endsWith('%');
  const bare = pattern.replace(/^%+|%+$/g, '');
  if (starts && ends)
    return [
      field(label),
      word(negated ? 'does not contain' : 'contains'),
      val(bare),
    ];
  if (ends)
    return [
      field(label),
      word(negated ? 'does not start with' : 'starts with'),
      val(bare),
    ];
  if (starts)
    return [
      field(label),
      word(negated ? 'does not end with' : 'ends with'),
      val(bare),
    ];
  return negated
    ? [field(label), word('other than'), val(bare)]
    : [field(`${label}:`), val(bare)];
}

// Renders one expression. Returns a Comparison when the shape is a plain column
// comparison (so it can take part in range merging), otherwise finished text.
function describe(
  raw: Expr,
  negated = false,
  labels?: ColumnLabels
): Comparison | TraitPart[] {
  const expr = unwrap(raw) ?? raw;

  // NOT arrives as a function node wrapping the clause, not as a unary operator.
  if (isNot(expr)) {
    const inner = firstArg(expr);
    return inner ? describe(inner, !negated, labels) : sqlish(expr, labels);
  }

  if (expr.type !== 'binary_expr') {
    const parts = sqlish(expr, labels);
    return negated ? [word('not'), ...parts] : parts;
  }

  const left = unwrap(expr.left);
  const { right } = expr;
  const rawOperator = expr.operator ?? '';

  if (rawOperator === 'OR' || rawOperator === 'AND') {
    // De Morgan. Negating the whole clause produced "not (a or b)", which is
    // one run of text with no field or value inside it to mark, and reads worse
    // than saying what each side has to be. Pushing the negation into the sides
    // and flipping the joiner says the same thing.
    const joinOperator = negated
      ? { OR: 'AND', AND: 'OR' }[rawOperator]
      : rawOperator;
    const sides = flattenOperands(expr, rawOperator).map((side) =>
      describe(side, negated, labels)
    );

    const collapsed = collapseSameColumn(sides, joinOperator);
    if (collapsed) return collapsed;

    const between = joinOperator === 'OR' ? 'or' : 'and';
    return sides
      .map(asParts)
      .filter((side) => side.length)
      .flatMap((side, i) => (i ? [word(between), ...side] : side));
  }

  const operator = negated
    ? NEGATED_OPERATOR[rawOperator] ?? rawOperator
    : rawOperator;

  if (left?.type !== 'column_ref' || !left.column) {
    const parts = sqlish(expr, labels);
    return negated ? [word('not'), ...parts] : parts;
  }
  const label = humanize(left.column, labels);

  if (operator === 'IS' || operator === 'IS NOT') {
    if (unwrap(right)?.type === 'null') {
      return [word(operator === 'IS' ? 'no' : 'has'), field(label)];
    }
    return [field(label), word(operator.toLowerCase()), val(literal(right))];
  }

  if (operator === 'LIKE' || operator === 'NOT LIKE') {
    return describeLike(
      label,
      String(unwrap(right)?.value ?? ''),
      operator !== 'LIKE'
    );
  }

  const value = literal(right, isYearColumn(label));

  if (
    (operator === '=' || operator === '<>' || operator === '!=') &&
    BOOLEAN_VALUES.includes(value.toLowerCase())
  ) {
    const truthy = value === '1' || value.toLowerCase() === 'true';
    return booleanFact(label, operator === '=' ? truthy : !truthy);
  }

  if (!COMPARISONS[operator]) {
    const parts = sqlish(expr, labels);
    return negated ? [word('not'), ...parts] : parts;
  }

  return {
    column: left.column,
    label,
    operator,
    value,
    values: literalList(right, isYearColumn(label)),
    numeric:
      unwrap(right)?.type === 'number' ? Number(right?.value) : undefined,
  };
}

// Last resort for a shape this does not model: show the SQL rather than drop the
// clause or guess at it. A missing condition would be a lie; raw SQL is only ugly.
function sqlish(node: Expr, labels?: ColumnLabels): TraitPart[] {
  const expr = unwrap(node) ?? node;
  if (expr.type === 'column_ref' && expr.column)
    return [field(humanize(expr.column, labels))];
  // A function this does not model, e.g. COALESCE(...). Naming it beats emitting
  // an empty side, which is how "=" ended up rendering as a bare trait.
  if (expr.type === 'function') {
    const inner = firstArg(expr);
    const arg = inner ? joinParts(asParts(describe(inner, false, labels))) : '';
    return [word(`${(expr.name ?? 'value').toLowerCase()}(${arg})`)];
  }
  if (expr.type === 'unary_expr' && expr.expr) {
    return [word('not'), ...asParts(describe(expr.expr, false, labels))];
  }
  if (expr.type === 'binary_expr') {
    const left = expr.left ? asParts(describe(expr.left, false, labels)) : [];
    const right = expr.right
      ? asParts(describe(expr.right, false, labels))
      : [];
    const operator = (expr.operator ?? '').toLowerCase();
    return [...left, ...(operator ? [word(operator)] : []), ...right];
  }
  return [val(literal(expr))];
}

function asParts(result: Comparison | TraitPart[]): TraitPart[] {
  return Array.isArray(result) ? result : COMPARISONS[result.operator](result);
}

function joinParts(parts: TraitPart[]): string {
  return parts
    .map((part) => part.text)
    .filter(Boolean)
    .join(' ');
}

const LOWER_BOUNDS = ['>=', '>'];
const UPPER_BOUNDS = ['<=', '<'];

// `employees >= 100 AND employees < 500` is one idea, not two. Merged so it reads
// as "employees 100 to 500" rather than as a pair of half-open bounds.
function mergeRanges(
  parts: (Comparison | TraitPart[])[]
): (Comparison | TraitPart[])[] {
  const merged: (Comparison | TraitPart[])[] = [];
  const used = new Set<number>();

  parts.forEach((part, i) => {
    if (used.has(i)) return;
    if (Array.isArray(part) || part.numeric === undefined) {
      merged.push(part);
      return;
    }
    const isLower = LOWER_BOUNDS.includes(part.operator);
    const isUpper = UPPER_BOUNDS.includes(part.operator);
    if (!isLower && !isUpper) {
      merged.push(part);
      return;
    }
    const pairIndex = parts.findIndex(
      (other, j) =>
        j > i &&
        !used.has(j) &&
        !Array.isArray(other) &&
        other.column === part.column &&
        other.numeric !== undefined &&
        (isLower
          ? UPPER_BOUNDS.includes(other.operator)
          : LOWER_BOUNDS.includes(other.operator))
    );
    if (pairIndex === -1) {
      merged.push(part);
      return;
    }
    used.add(i);
    used.add(pairIndex);
    const pair = parts[pairIndex] as Comparison;
    const [low, high] = isLower ? [part, pair] : [pair, part];
    merged.push([
      field(part.label),
      val(low.value),
      word('to'),
      val(high.value),
    ]);
  });

  return merged;
}

/**
 * Splits a node condition into one trait per requirement, in plain English.
 * Never throws: SQL it cannot read comes back verbatim, flagged `unparsed`.
 *
 * `labels` names columns the caller has a better word for than this file can derive.
 */
export function conditionTraits(
  sql?: string | null,
  labels?: ColumnLabels
): ConditionTrait[] {
  const trimmed = sql?.trim();
  if (!trimmed) return [];
  try {
    const ast = parser.astify(`SELECT 1 FROM t WHERE ${trimmed}`, {
      database: 'postgresql',
    });
    const statement = Array.isArray(ast) ? ast[0] : ast;
    const where = (statement as { where?: Expr })?.where;
    if (!where)
      return [{ text: trimmed, parts: [word(trimmed)], unparsed: true }];
    const described = flattenAnd(where).map((part) =>
      describe(part, false, labels)
    );
    const traits = mergeRanges(described)
      .map(asParts)
      .map((parts) => ({ parts, text: joinParts(parts).trim() }))
      .filter((trait) => trait.text);
    return traits.length
      ? traits
      : [{ text: trimmed, parts: [word(trimmed)], unparsed: true }];
  } catch {
    return [{ text: trimmed, parts: [word(trimmed)], unparsed: true }];
  }
}

/** The same traits as one sentence, for places with no room for chips. */
export function conditionSentence(sql?: string | null): string {
  return conditionTraits(sql)
    .map((trait) => trait.text)
    .join(' and ');
}
